import { Controller } from "@hotwired/stimulus"

/**
 * AI Playground Controls Controller — CONEK-3365 Wave 4.
 *
 * Owns the left controls rail of the admin playground:
 * - Default model dropdown (informational)
 * - System prompt override textarea + byte counter + revert button
 * - Tool toggle filter (client-side substring filter on `[data-tool-name]`)
 * - Latency display (last / avg10 / p95 over a rolling 30-sample buffer)
 * - Compare-mode toggle (dispatches a CustomEvent on document for the center
 *   pane to spawn a sibling `<section data-pane="secondary">` instance)
 * - Share-permalink button (copies window.location to clipboard)
 *
 * Compare-mode uses a single document-level `playground:compare-toggle` event
 * (the only cross-controller seam — `playground:send-prompt` was deleted in
 * planner v4 because each ai--companion instance owns its own composer).
 */
export default class extends Controller {
  static targets = [
    "defaultModelSelect",
    "systemPromptOverride",
    "overrideByteCount",
    "overrideDirtyDot",
    "toolFilter",
    "toolToggle",
    "latencyLast",
    "latencyAvg10",
    "latencyP95",
    "compareToggleButton",
  ]

  static values = {
    overrideMaxBytes: { type: Number, default: 8192 },
  }

  connect() {
    this.latencySamples = []
    this.compareModeEnabled = false
    this.boundOnLatency = (event) => this.recordLatency(event.detail?.ms ?? 0)
    document.addEventListener("playground:latency-sample", this.boundOnLatency)
  }

  disconnect() {
    document.removeEventListener(
      "playground:latency-sample",
      this.boundOnLatency,
    )
  }

  // ──────────────────────────────────────────────────────────────────────
  // System prompt override

  countOverrideBytes() {
    if (!this.hasSystemPromptOverrideTarget) return
    const text = this.systemPromptOverrideTarget.value
    const bytes = new TextEncoder().encode(text).length
    if (this.hasOverrideByteCountTarget) {
      this.overrideByteCountTarget.textContent = `${bytes} / ${this.overrideMaxBytesValue} bytes`
    }
    if (this.hasOverrideDirtyDotTarget) {
      this.overrideDirtyDotTarget.classList.toggle("hidden", bytes === 0)
    }
  }

  revertSystemPrompt() {
    if (!this.hasSystemPromptOverrideTarget) return
    this.systemPromptOverrideTarget.value = ""
    this.countOverrideBytes()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Tool toggle filter

  filterTools() {
    if (!this.hasToolFilterTarget) return
    const query = this.toolFilterTarget.value.trim().toLowerCase()
    const labels = this.element.querySelectorAll("[data-tool-name]")
    labels.forEach((label) => {
      const name = (label.dataset.toolName || "").toLowerCase()
      const match = query === "" || name.includes(query)
      label.classList.toggle("hidden", !match)
    })
  }

  // ──────────────────────────────────────────────────────────────────────
  // Latency

  recordLatency(ms) {
    if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return
    this.latencySamples.push(ms)
    if (this.latencySamples.length > 30) {
      this.latencySamples.shift()
    }
    if (this.hasLatencyLastTarget) {
      this.latencyLastTarget.textContent = `${Math.round(ms)} ms`
    }
    if (this.hasLatencyAvg10Target) {
      const recent = this.latencySamples.slice(-10)
      const avg = recent.reduce((a, b) => a + b, 0) / recent.length
      this.latencyAvg10Target.textContent = `${Math.round(avg)} ms`
    }
    if (this.hasLatencyP95Target) {
      if (this.latencySamples.length < 30) {
        this.latencyP95Target.textContent = `pending (${this.latencySamples.length}/30)`
      } else {
        const sorted = [...this.latencySamples].sort((a, b) => a - b)
        // Nearest-rank p95: index = ceil(N * 0.95) - 1. For N=30 → index 28.
        const p95 = sorted[Math.ceil(sorted.length * 0.95) - 1]
        this.latencyP95Target.textContent = `${Math.round(p95)} ms`
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Compare mode

  toggleCompareMode() {
    this.compareModeEnabled = !this.compareModeEnabled
    document.dispatchEvent(
      new CustomEvent("playground:compare-toggle", {
        detail: { enabled: this.compareModeEnabled },
      }),
    )
    if (this.hasCompareToggleButtonTarget) {
      this.compareToggleButtonTarget.textContent = this.compareModeEnabled
        ? this.compareToggleButtonTarget.dataset.offLabel || "Exit compare mode"
        : this.compareToggleButtonTarget.dataset.onLabel || "Compare models"
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Share permalink

  sharePermalink(event) {
    if (event) event.preventDefault()
    const primaryPane = document.querySelector('[data-pane="primary"]')
    const chatId = primaryPane?.dataset.aiCompanionChatIdValue
    const url = new URL(window.location.href)
    if (chatId) url.searchParams.set("chat", chatId)
    navigator.clipboard?.writeText(url.toString())
  }
}
